import { useInfiniteQuery } from "@tanstack/react-query";
import type {
  InfiniteData,
  QueryKey,
  UseInfiniteQueryResult,
} from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type { CasinoGame, CasinoGamesResponse } from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { normalizeCasinoGameFromApi } from "./casinoGameNormalizers";
import { retryOnTransientErrors } from "./queryUtils";

export const GAME_PASS_CASINO_GAMES_QUERY_KEY = [
  "game-pass",
  "casino-games",
] as const;

/** Page size for the casino games endpoint. Mirrors the BE default and the
 *  spec'd `limit=50`; one page is fetched per "Show More". */
export const CASINO_GAMES_PAGE_SIZE = 50;

export type CasinoGamesFilter = {
  /** List one provider's games (mutually exclusive with `search`). */
  provider?: string;
  /** Case-insensitive name search across every provider. */
  search?: string;
  /** Override the page size (defaults to {@link CASINO_GAMES_PAGE_SIZE}). */
  limit?: number;
};

/** Normalise an API page so consumers always read the same shape even when the
 *  payload arrives bare or wrapped in `{ success, data }`. */
const unwrapGamesPage = (
  payload: unknown,
  offset: number,
  limit: number,
): CasinoGamesResponse => {
  const root =
    payload && typeof payload === "object"
      ? ((payload as Record<string, unknown>).data &&
        typeof (payload as Record<string, unknown>).data === "object" &&
        !Array.isArray((payload as Record<string, unknown>).data)
          ? ((payload as Record<string, unknown>).data as Record<string, unknown>)
          : (payload as Record<string, unknown>))
      : {};
  const games = Array.isArray(root.games)
    ? (root.games as unknown[])
        .map(normalizeCasinoGameFromApi)
        .filter((game): game is CasinoGame => game !== null)
    : [];
  return {
    games,
    filtered_total:
      typeof root.filtered_total === "number"
        ? root.filtered_total
        : games.length,
    unfiltered_total:
      typeof root.unfiltered_total === "number" ? root.unfiltered_total : 0,
    limit: typeof root.limit === "number" ? root.limit : limit,
    offset: typeof root.offset === "number" ? root.offset : offset,
  };
};

export const fetchCasinoGames = async (
  filter: CasinoGamesFilter,
  offset: number,
): Promise<CasinoGamesResponse> => {
  const limit = filter.limit ?? CASINO_GAMES_PAGE_SIZE;
  // Param order mirrors the Postman/spec route
  // (`?provider=…&limit=…&offset=…`); axios serialises in insertion order.
  const params: Record<string, string | number> = {};
  if (filter.search) params.search = filter.search;
  else if (filter.provider) params.provider = filter.provider;
  params.limit = limit;
  params.offset = offset;

  const response = await getPrivateInstance().get<CasinoGamesResponse>(
    API_ROUTES.GET_GAME_PASS_CASINO_GAMES,
    { params },
  );
  return unwrapGamesPage(response.data, offset, limit);
};

/** Build the next `offset` for the "Show More" pagination, or `undefined` when
 *  every matching game has been loaded. Exported so the page-walking maths can
 *  be unit-tested without a live query. */
export const getNextCasinoGamesOffset = (
  lastPage: CasinoGamesResponse,
): number | undefined => {
  const nextOffset = lastPage.offset + lastPage.games.length;
  return nextOffset < lastPage.filtered_total ? nextOffset : undefined;
};

/**
 * Infinite-query the casino games endpoint
 * (`GET /game-pass-admin/v2/admin/casino-games`) for either a single provider's
 * games (`filter.provider`) or a cross-provider name search (`filter.search`).
 *
 * Each "Show More" fetches the next `limit`-sized page (offset-based), and
 * `getNextCasinoGamesOffset` stops once `filtered_total` games have been
 * loaded. Disabled until a provider or search term is supplied so the network
 * isn't hit for the idle (collapsed) picker.
 */
export const useGetCasinoGames = (
  filter: CasinoGamesFilter,
  enabled = true,
): UseInfiniteQueryResult<InfiniteData<CasinoGamesResponse>, Error> => {
  const limit = filter.limit ?? CASINO_GAMES_PAGE_SIZE;
  const isEnabled =
    enabled && Boolean(filter.search?.trim() || filter.provider);

  return useInfiniteQuery<
    CasinoGamesResponse,
    Error,
    InfiniteData<CasinoGamesResponse>,
    QueryKey,
    number
  >({
    queryKey: [
      ...GAME_PASS_CASINO_GAMES_QUERY_KEY,
      filter.provider ?? null,
      filter.search?.trim() ?? null,
      limit,
    ],
    queryFn: ({ pageParam }) => fetchCasinoGames(filter, pageParam),
    initialPageParam: 0,
    getNextPageParam: getNextCasinoGamesOffset,
    enabled: isEnabled,
    staleTime: 1000 * 60,
    retry: retryOnTransientErrors,
    refetchOnWindowFocus: false,
  });
};
